import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  User,
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
  linkWithCredential,
  signInWithCredential,
  deleteUser,
  getAuth,
  UserCredential,
  getAdditionalUserInfo,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { AppStateService } from './app-state.service';
import { StripeService } from './stripe.service';
import { GoogleAdsService } from './google-ads.service';
import posthog from 'posthog-js';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private googleProvider = new GoogleAuthProvider();

  private userSubject = new BehaviorSubject<User | null | undefined>(undefined);

  // Expose the user as an observable.
  public readonly user$: Observable<User | null> = this.userSubject
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  constructor(
    private firebaseService: FirebaseService,
    private ngZone: NgZone,
    private appState: AppStateService,
    private stripeService: StripeService,
    private googleAdsService: GoogleAdsService,
  ) {
    this.googleProvider.addScope('profile');
    this.googleProvider.addScope('email');
    // Listen to Firebase auth state changes.
    onAuthStateChanged(this.firebaseService.auth, (user) => {
      this.ngZone.run(() => {
        this.userSubject.next(user);
      });
    });
  }

  // Create a new account with email/password and add user to Firestore
  async createAccount(email, password, firstName, lastName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        this.firebaseService.auth,
        email,
        password,
      );
      const user = userCredential.user;

      const userDocRef = doc(this.firebaseService.firestore, 'users', user.uid);

      await setDoc(
        userDocRef,
        this.buildDefaultUserProfile({
          id: user.uid,
          email: user.email,
          firstName,
          lastName,
          displayName: `${firstName} ${lastName}`,
          emailVerified: false,
          preferences: {
            children: {
              preference: false,
              usage: false,
            },
            pets: {
              preference: false,
              usage: false,
            },
            quiet: {
              preference: false,
              usage: false,
            },
          },
        }),
        { merge: true },
      );
      posthog.capture('user_signed_up', {
        email: lastName,
        lastName: true
      });
      this.googleAdsService.reportSignupConversion();
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  // Sign in with email and password
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.firebaseService.auth,
        email,
        password,
      );
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  // Send password reset email
  async forgotPassword(email) {
    try {
      await sendPasswordResetEmail(this.firebaseService.auth, email);
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw error;
    }
  }

  // Confirm password reset with oob code
  async confirmPasswordReset(oobCode: string, newPassword: string) {
    try {
      await confirmPasswordReset(
        this.firebaseService.auth,
        oobCode,
        newPassword,
      );
    } catch (error) {
      console.error('Error confirming password reset:', error);
      throw error;
    }
  }

  /** Used on your /register page to auto-provision new Google users + profile */
  async registerWithGoogle(): Promise<UserCredential> {
    const result = await signInWithPopup(
      this.firebaseService.auth,
      this.googleProvider
    );

    // Create Firestore profile if missing
    const user = result.user;
    const ref = doc(this.firebaseService.firestore, 'users', user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const [firstName, ...rest] = (user.displayName || '').split(' ');
      await setDoc(
        ref,
        this.buildDefaultUserProfile({
          id: user.uid,
          email: user.email!,
          firstName: firstName || 'Google',
          lastName: rest.join(' ') || 'User',
          displayName: user.displayName || `${firstName} ${rest.join(' ')}`,
          emailVerified: user.emailVerified
        }),
        { merge: true }
      );
      this.googleAdsService.reportSignupConversion();
    }

    return result;
  }

  /** Used in LoginComponent to BLOCK brand-new Google users */
  async signInWithGoogle(): Promise<UserCredential> {
    const result = await signInWithPopup(
      this.firebaseService.auth,
      this.googleProvider
    );

    const info = getAdditionalUserInfo(result);
    // If Firebase just spun up a brand-new user, tear it down immediately:
    if (info?.isNewUser) {
      await deleteUser(result.user);                // delete stub auth user
      await this.firebaseService.auth.signOut();    // clear state
      throw new Error('no-account');                // bubble up to component
    }

    // Otherwise existing user → OK to proceed
    return result;
  }

  private buildDefaultUserProfile(overrides: Partial<any> = {}) {
    return {
      createdAt: serverTimestamp(),
      emailVerified: false,
      mobileNumber: null,
      isMobileVerified: false,
      isActive: true,
      accountHealth: {
        status: 'positive',
        lastUpdated: serverTimestamp(),
        backToPositiveOnDate: null,
      },
      memberships: [],
      preferences: null,
      linkedInAccount: null,
      ...overrides,
    };
  }

  // Get current user
  getCurrentUser() {
    return this.firebaseService.auth.currentUser;
  }

  public isAuthenticated(): boolean {
    return this.userSubject.getValue() !== null;
  }

  // Sign out
  async signOut() {
    try {
      await this.firebaseService.auth.signOut();
      this.appState.resetAppState();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  async needsReauth(thresholdMinutes = 5): Promise<boolean> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');

    // Fetch the ID token result, which includes authTime
    const tokenResult = await user.getIdTokenResult();
    const authTimeSec = tokenResult.authTime
      ? new Date(tokenResult.authTime).getTime()
      : 0;
    const ageMs = Date.now() - authTimeSec;

    // If token is older than threshold, require reauth
    return ageMs > thresholdMinutes * 60 * 1000;
  }

  async deleteAccount(): Promise<void> {
    if (await this.needsReauth()) {
      throw new Error('Reauthentication required');
    }

    const user: User | null = this.firebaseService.auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in.');
    }

    const profile = this.appState.user.profile();
    const sub = this.appState.user.subscription();

    if (sub?.isSubscribed) {
      try {
        await firstValueFrom(this.stripeService.cancelSubscription());
      } catch (err) {
        console.error('Error canceling subscription:', err);
      }
    }

    const mobile = profile?.mobileNumber;
    if (mobile) {
      const numberId = mobile.startsWith('+1') ? mobile.slice(2) : mobile;
      try {
        const docRef = doc(this.firebaseService.firestore, `verified-numbers`, numberId);
        await deleteDoc(docRef);
      } catch (err) {
        console.error('Error removing verified number:', err);
      }
    }

    const userRef = doc(this.firebaseService.firestore, 'users', user.uid);
    await updateDoc(userRef, {
      isActive: false,
      accountDeletedOn: serverTimestamp(),
    });

    await deleteUser(user);

    await this.signOut();
    this.appState.resetAppState();
  }
}
